import { apiUrl } from "../api/endpoints";
import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import '../Components/modal.css';

import { useAuth } from '../hooks/AuthProvider';
import Alert from '../Components/Alert';

import { LanguageContext } from '../context/LanguageContext';
import orgTranslations from '../languages/organization.json';
import { getErrorMessage } from '../utils/errorUtils';
import { useFontSize } from '../context/FontSizeContext';

import { AddNewIcon, DescriptionIcon } from '../svg/UtilsIcons';
import { OrgNameIcon } from '../svg/OrganizationIcons';
import Select from 'react-select';
import { normalizeLocationListRow } from '../utils/listRowIds';

export const AddOrganization = ({
    setOpen,
    refresh,
    bg,
    fg,
    bd,
    ft,
    btnPrimary,
    btnSecondary,
    textColor
}) => {
    const { org, permissions } = useAuth();
    const { lang } = useContext(LanguageContext);
    useFontSize(); 

    const canCreateOrganizations = permissions['create_organizations'];
    const canViewLocations = permissions['view_locations'];

    const t = orgTranslations[lang].AddOrganization;
    const isRtl = ['ar', 'ur'].includes(lang);

    const [submitData, setSubmitData] = useState({
        name: '',
        description: '',
        location_id: null
    });
    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right'
    });

    const showAlert = (type, message) => {
        setAlertState({
            open: true,
            type,
            message,
            position: isRtl ? 'left' : 'right'
        });
    };

    useEffect(() => {
        if (!canCreateOrganizations) return;

        const modal = document.getElementById('modal');
        if (modal) {
            if (fg) modal.style.setProperty('--fg', fg);
            if (bd) modal.style.setProperty('--bd', bd);
            if (btnSecondary)
                modal.style.setProperty('--secondary', btnSecondary);
        }
    }, [bd, btnSecondary, fg, canCreateOrganizations]);

    useEffect(() => {
        if (!canCreateOrganizations || !canViewLocations) return;
        let mounted = true;
        setLocationsLoading(true);
        axios.get(apiUrl('location/options'))
            .then((res) => {
                if (!mounted) return;
                const rows = Array.isArray(res?.data?.data) ? res.data.data.map(normalizeLocationListRow) : [];
                setLocations(rows);
            })
            .catch(() => { if (mounted) setLocations([]); })
            .finally(() => { if (mounted) setLocationsLoading(false); });
        return () => { mounted = false; };
    }, [canCreateOrganizations, canViewLocations]);

    const createOrganization = async () => {
        if (submitting) return;

        if (!submitData.name || submitData.name.trim() === '') {
            showAlert(
                'error',
                t.validation?.nameRequired ||
                    'Organization name is required.'
            );
            return;
        }

        const body = {
            ...submitData,
            name: submitData.name.trim(),
            parent: org.id
        };

        setSubmitting(true);
        try {
            const res = await axios.post(
                apiUrl(`organization/create`),
                body,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            showAlert(
                'success',
                res.data.message || 'Organization created successfully.'
            );

            refresh();
            setTimeout(() => {
                setOpen(false);
            }, 2000);
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            showAlert('error', errorMessage);
            setSubmitting(false);
        }
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setSubmitData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    if (!canCreateOrganizations) {
        return (
            <div
                id="modal"
                style={{
                    direction: lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr'
                }}
            >
                <div
                    id="popup"
                    className="dashboard-modal"
                    style={{
                        animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                        fontSize:
                            'calc(var(--body-font-size) * var(--font-size-multiplier))'
                    }}
                >
                    <div className="dashboard-header">
                        <div className="dashboard-header-icon">
                            <AddNewIcon />
                        </div>
                        <div className="dashboard-header-text">
                            <h2>
                                {t.permissionDenied?.title || 'Permission Denied'}
                            </h2>
                            <p>
                                {t.permissionDenied?.message ||
                                    'You do not have permission to create a new organization.'}
                            </p>
                        </div>
                    </div>

                    <div className="modal-divider" />

                    <div className="modal-content dashboard-content" />

                    <div className="modal-footer dashboard-footer">
                        <button
                            className="btn btn-cancel"
                            onClick={() => setOpen(false)}
                        >
                            {t.buttonClose || 'Close'}
                        </button>
                        <div />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            id="modal"
            style={{
                direction: lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr'
            }}
        >
            <Alert
                open={alertState.open}
                close={() =>
                    setAlertState(prev => ({ ...prev, open: false }))
                }
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />

            <div
                id="popup"
                className="dashboard-modal"
                style={{
                    animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                    fontSize:
                        'calc(var(--body-font-size) * var(--font-size-multiplier))'
                }}
            >
                <div className="dashboard-header">
                    <div className="dashboard-header-icon">
                        <AddNewIcon />
                    </div>
                    <div className="dashboard-header-text">
                        <h2>{t.title}</h2>
                        <p>
                            {t.subtitle ||
                                'Create new Organization by filling in the form below'}
                        </p>
                    </div>
                </div>

                <div className="modal-divider" />

                <div className="modal-content dashboard-content">
                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <OrgNameIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.labelName}
                            </span>
                        </div>

                        <div className="form-item">
                            <div className="field-with-counter">
                                <input
                                    type="text"
                                    name="name"
                                    className="add-column"
                                    placeholder={t.helperName}
                                    maxLength={50}
                                    value={submitData.name}
                                    onChange={handleChange}
                                />
                                <span className="field-counter">
                                    {submitData.name?.length || 0} / 50
                                </span>
                            </div>
                        </div>
                    </div>

                    {canViewLocations ? (
                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-title-text">{t.labelLocation || 'Location'}</span>
                            </div>
                            <div className="form-item">
                                <Select
                                    isClearable
                                    isSearchable
                                    isLoading={locationsLoading}
                                    classNamePrefix="react-select"
                                    placeholder={t.placeholderLocation || 'Select location (optional)'}
                                    options={locations.map((location) => ({ value: location.id, label: location.address ? `${location.name} — ${location.address}` : (location.name || `#${location.id}`) }))}
                                    value={locations.map((location) => ({ value: location.id, label: location.address ? `${location.name} — ${location.address}` : (location.name || `#${location.id}`) })).find((option) => Number(option.value) === Number(submitData.location_id)) || null}
                                    onChange={(option) => setSubmitData((prev) => ({ ...prev, location_id: option ? option.value : null }))}
                                />
                            </div>
                        </div>
                    ) : null}

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <DescriptionIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.labelDescription}
                            </span>
                        </div>

                        <div className="form-item">
                            <textarea
                                name="description"
                                rows={5}
                                className="add-column"
                                placeholder={t.placeholderDescription}
                                value={submitData.description}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>

                <div className="modal-footer dashboard-footer">
                    <button
                        className="btn btn-cancel"
                        onClick={() => setOpen(prev => !prev)}
                        disabled={submitting}
                    >
                        {t.buttonCancel}
                    </button>
                    <button
                        className="btn btn-apply dashboard-primary-btn"
                        onClick={createOrganization}
                        disabled={submitting}
                    >
                        {t.buttonCreate}
                    </button>
                </div>
            </div>
        </div>
    );
};
